import { Socket, Channel } from 'phoenix';
import { useState, useEffect, useRef } from 'react';
import { getUserAuth } from '../core/_axios';
import { BASE_WSS_URL } from '../core/_requests';
import { useAuth } from '../AuthContext';
import { EmailCampaign } from '../core/_models_email';

interface TrainingProgress {
  completed_count: number;
  required_count: number;
  is_complete: boolean;
  remaining: number;
}

interface UseEmailCampaignWebSocketProps {
  onCampaignCreated?: (campaign: EmailCampaign) => void;
  onCampaignUpdated?: (campaign: EmailCampaign) => void;
  onTrainingProgressUpdated?: (trainingProgress: TrainingProgress) => void;
}

// Singleton socket instance to prevent multiple connections
let sharedSocket: Socket | null = null;
let sharedChannel: Channel | null = null;
let connectionCount = 0;

export default function useEmailCampaignWebSocket({
  onCampaignCreated,
  onCampaignUpdated,
  onTrainingProgressUpdated
}: UseEmailCampaignWebSocketProps = {}) {
  const { currentUserAccountUniqueId, viewingAllAccounts, currentUser } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  
  // Use refs to store the latest callbacks without triggering re-renders
  const onCampaignCreatedRef = useRef(onCampaignCreated);
  const onCampaignUpdatedRef = useRef(onCampaignUpdated);
  const onTrainingProgressUpdatedRef = useRef(onTrainingProgressUpdated);
  
  // Update refs when callbacks change
  useEffect(() => {
    onCampaignCreatedRef.current = onCampaignCreated;
  }, [onCampaignCreated]);
  
  useEffect(() => {
    onCampaignUpdatedRef.current = onCampaignUpdated;
  }, [onCampaignUpdated]);
  
  useEffect(() => {
    onTrainingProgressUpdatedRef.current = onTrainingProgressUpdated;
  }, [onTrainingProgressUpdated]);

  useEffect(() => {
    // When viewing all accounts, we can't subscribe to WebSocket events
    // because we don't know which account will create the campaign
    if (!currentUserAccountUniqueId && viewingAllAccounts) {
      console.log('[EmailCampaignWebSocket] Viewing all accounts - WebSocket not available');
      // Set as not connected but don't block functionality
      setIsConnected(false);
      return;
    }
    
    if (!currentUserAccountUniqueId) {
      return;
    }

    connectionCount++;
    console.log(`[EmailCampaignWebSocket] Component mounted, connection count: ${connectionCount}`);

    // Create unique handler functions for this component instance
    const uniqueId = Math.random().toString(36).substr(2, 9);
    
    const campaignCreatedHandler = (data: any) => {
      console.log(`[EmailCampaignWebSocket-${uniqueId}] Campaign created event:`, data);
      if (onCampaignCreatedRef.current && data.campaign) {
        onCampaignCreatedRef.current(data.campaign);
      }
    };
    
    const campaignUpdatedHandler = (data: any) => {
      console.log(`[EmailCampaignWebSocket-${uniqueId}] Campaign updated event:`, data);
      // Handle both payload formats
      const campaign = data.campaign || data.payload?.campaign;
      if (onCampaignUpdatedRef.current && campaign) {
        onCampaignUpdatedRef.current(campaign);
      }
    };
    
    const trainingProgressHandler = (data: any) => {
      console.log(`[EmailCampaignWebSocket-${uniqueId}] Training progress updated event:`, data);
      if (onTrainingProgressUpdatedRef.current && data.payload?.training_progress) {
        onTrainingProgressUpdatedRef.current(data.payload.training_progress);
      }
    };

    // If we already have a connection, just add listeners
    if (sharedSocket && sharedChannel) {
      console.log('[EmailCampaignWebSocket] Reusing existing WebSocket connection');
      
      sharedChannel.on('email_campaign_created', campaignCreatedHandler);
      sharedChannel.on('email_campaign_updated', campaignUpdatedHandler);
      sharedChannel.on('training_progress_updated', trainingProgressHandler);
      
      setIsConnected(true);
      
      // Cleanup function - don't use off() on shared channel
      return () => {
        connectionCount--;
        console.log(`[EmailCampaignWebSocket] Component unmounted, connection count: ${connectionCount}`);
        
        // Only disconnect if this was the last component using the connection
        if (connectionCount === 0 && sharedChannel && sharedSocket) {
          console.log('[EmailCampaignWebSocket] Last component unmounted, closing WebSocket connection');
          sharedChannel.leave();
          sharedSocket.disconnect();
          sharedChannel = null;
          sharedSocket = null;
        }
      };
    }

    // Create new connection
    console.log('[EmailCampaignWebSocket] Creating new WebSocket connection');
    
    const socket = new Socket(`${BASE_WSS_URL}/socket/web`, {
      params: {
        web_token: getUserAuth()?.api_token
      }
    });

    const accountChannel = socket.channel(`web:account:${currentUserAccountUniqueId}`, {
      from_web: true
    });

    // Store references
    sharedSocket = socket;
    sharedChannel = accountChannel;

    // Set up event handlers with unique ID for debugging
    const newConnectionCampaignCreatedHandler = (data: any) => {
      console.log(`[EmailCampaignWebSocket-new-${uniqueId}] Campaign created event:`, data);
      if (onCampaignCreatedRef.current && data.campaign) {
        onCampaignCreatedRef.current(data.campaign);
      }
    };
    
    const newConnectionCampaignUpdatedHandler = (data: any) => {
      console.log(`[EmailCampaignWebSocket-new-${uniqueId}] Campaign updated event:`, data);
      // Handle both payload formats
      const campaign = data.campaign || data.payload?.campaign;
      if (onCampaignUpdatedRef.current && campaign) {
        onCampaignUpdatedRef.current(campaign);
      }
    };
    
    const newConnectionTrainingProgressHandler = (data: any) => {
      console.log(`[EmailCampaignWebSocket-new-${uniqueId}] Training progress updated event:`, data);
      if (onTrainingProgressUpdatedRef.current && data.payload?.training_progress) {
        onTrainingProgressUpdatedRef.current(data.payload.training_progress);
      }
    };

    accountChannel.on('email_campaign_created', newConnectionCampaignCreatedHandler);
    accountChannel.on('email_campaign_updated', newConnectionCampaignUpdatedHandler);
    accountChannel.on('training_progress_updated', newConnectionTrainingProgressHandler);
    
    socket.connect();
    
    accountChannel.join()
      .receive("ok", resp => {
        if (resp.connection_error) {
          console.error("[EmailCampaignWebSocket] Connection error", resp.connection_error);
          setIsConnected(false);
          return;
        }
        setIsConnected(true);
        console.log("[EmailCampaignWebSocket] Successfully joined email campaign channel");
      })
      .receive("error", resp => {
        console.error("[EmailCampaignWebSocket] Failed to join channel", resp);
        setIsConnected(false);
      });

    return () => {
      connectionCount--;
      console.log(`[EmailCampaignWebSocket] Component unmounted, connection count: ${connectionCount}`);
      
      // Only disconnect if this was the last component using the connection
      if (connectionCount === 0) {
        console.log('[EmailCampaignWebSocket] Last component unmounted, closing WebSocket connection');
        accountChannel.leave();
        socket.disconnect();
        sharedChannel = null;
        sharedSocket = null;
      }
    };
  }, [currentUserAccountUniqueId]); // Only re-run when account changes

  return {
    isConnected
  };
}